import { Client as ConversationsClient, Conversation, Message, Participant, JSONValue } from '@twilio/conversations'
import { EventEmitter } from 'events'
import chatClientInterface from './chatClient'
import { MessageEventResponse } from './types'
import { captureException, addBreadcrumb } from '@sentry/browser'

class TwilioChatClient extends EventEmitter implements chatClientInterface {

  remoteClient: ConversationsClient | null = null
  token: string
  joinedConversations: Conversation[] = []

  constructor (token: string) {

    super()
    this.token = token
  }

  static create = async (token): Promise<TwilioChatClient> => {

    const client = new TwilioChatClient(token)

    await client.initialise()

    return client

  }

  disconnect = (): void => {

    this.emit('disconnect')

  }

  getJoinedConversations = (conversationName: string): Conversation | undefined =>
    this.joinedConversations.find((conversation: Conversation) => conversationName  === conversation.uniqueName)


  getConversationOrJoin = async (conversationName: string) => {
    let conversation = null
    conversation = this.getJoinedConversations(conversationName)
    if (!conversation) {

      conversation = await this.joinConversation(conversationName)

    }
    return conversation
  }

  initialise = async (): Promise<boolean> => {

    try {
      const client = await ConversationsClient.create(this.token)
      this.remoteClient = client

      this.remoteClient.on('connectionStateChanged', (data) => {

        if (data === 'connecting') this.emit('disconnect')
        if (data === 'connected') this.emit('connect')

      })

      this.remoteClient.on('connectionError', (data) => {

        if (data.terminal) this.emit('disconnect')

      })

      return true
    } catch (e) {

      addBreadcrumb({
        message: 'could not create the conversation client'
      })
      captureException(e)

      return false

    }

    return true

  }

  typing = async (conversationName: string): Promise<boolean> => {

    const conversation = await this.getConversationOrJoin(conversationName)
    if (conversation) {

      await conversation.typing()
      return true
    }
    else {

      throw new Error('Conversation not found')

    }

  }

  sendMessage = async (conversationName: string, message: string, messageAttributes: JSONValue): Promise<number | Error> => {

    const conversation = await this.getConversationOrJoin(conversationName)
    if (conversation) {

      const messageIndex = await conversation.sendMessage(message, messageAttributes)
      return messageIndex
    }
    else {

      throw new Error('Conversation not found in sendMessage')
    }

  }

  getMessages = async (conversationName: string, limit = 30): Promise<Message[]> => {

    const conversation = await this.getConversationOrJoin(conversationName)
    if (!conversation) throw new Error('Conversation not found in getMessages')

    const messages = await conversation.getMessages(limit)
    return messages.items

  }

  getLastReadMessageIndex = async (conversationName: string, author: string): Promise<number | Error> => {

    const conversation = await this.getConversationOrJoin(conversationName)
    if (!conversation) throw new Error('Conversation not found in getLastReadMessage')

    let participants: Participant[] = []

    try {

      participants = await conversation.getParticipants()
    }
    catch (e) {

      throw new Error(`Couldn't fetch members`)
    }

    const [ participant ] = participants.filter(({ identity }) => identity === author)

    if (participant) {

      const { lastReadMessageIndex } = participant

      if (lastReadMessageIndex) return lastReadMessageIndex
      else return -1

    }
    else return -1

  }

  setMessagesRead = async (conversationName: string, lastConsumedMessageIndex?: number) => {

    const conversation = await this.getConversationOrJoin(conversationName)
    if (!conversation) throw new Error('Conversation not found in the setMessagesRead')

    await conversation.setAllMessagesRead()

    return true

  }

  formatMessageResponse = (message: Message): MessageEventResponse => ({
    author: message.author ? message.author : '',
    conversationName: message.conversation.uniqueName ? message.conversation.uniqueName : '',
    body: message.body ? message.body : '',
    attributes: message.attributes
  })

  checkUserIsInConversation = async (conversationName: string, id: string): Promise<boolean> => {

    if (!this.remoteClient) return false
    let conversation: Conversation

    try {

      conversation = await this.remoteClient.getConversationByUniqueName(conversationName)

    } catch(e) {

      addBreadcrumb({
        message: 'Conversation not found'
      })
      captureException(e)
      return false

    }

    try {

      const isUserIn = await conversation.getParticipantByIdentity(id)

      if (isUserIn) return true

    } catch (e) {

      return false

    }

    return false

  }

  addUserToConversation = async (conversationName: string, id: string, token?: string): Promise<boolean> => {

    if (!this.remoteClient) return false
    let conversation: Conversation

    try {

      conversation = await this.remoteClient.getConversationByUniqueName(conversationName)

    } catch (e) {

      addBreadcrumb({
        message: 'Could not find the conversation'
      })
      captureException(e)
      return false

    }

    if (!conversation) return false

    try {

      await conversation.add(id)
      return true

    } catch (e) {

      if (token) return this.createConversationClientForNewUser(token, conversation, id)
      return false

    }

  }

  // if a user is newly registered they will need to create a conversation
  // client in order to be added to conversations in the future
  createConversationClientForNewUser = async (token: string, conversation: Conversation, id: string): Promise<boolean> => {

    let conversationClient: ConversationsClient

    try {

      conversationClient = await ConversationsClient.create(token)

    } catch (e) {

      addBreadcrumb({
        message: 'Could not create conversation client for user'
      })
      captureException(e)
      return false

    }

    if (!conversationClient) return false

    try {

      await conversation.add(id)
      return true

    } catch (e) {

      addBreadcrumb({
        message: 'Could not add the user to the conversation'
      })
      captureException(e)
      return false

    }
  }

  createNewConversation = async (channelName: string, id: string): Promise<string | null> => {

    if (this.remoteClient === null) return null

    let conversation: Conversation | null = null

    try {

      conversation = await this.remoteClient.createConversation({
        uniqueName: channelName + '-conversation',
        friendlyName: 'Chat Channel ' + channelName
      })


    } catch (e) {


        let isUserIn = false

        try {

          isUserIn = await this.checkUserIsInConversation(channelName + '-conversation', id)
          const existingConversation = await this.remoteClient.getConversationByUniqueName(channelName + '-conversation')
          return existingConversation.uniqueName

        } catch (e) {

          addBreadcrumb({
            message: 'user is not in the conversation'
          })

        }

        if (!isUserIn) {

          try {

            await this.addUserToConversation(channelName + '-conversation', id)
            const existingConversation = await this.remoteClient.getConversationByUniqueName(channelName + '-conversation')
            this.joinedConversations.push(existingConversation)
            return existingConversation.uniqueName

          } catch (e) {

            console.log('the user was not added: ', e)

            addBreadcrumb({
              message: 'user not added to a conversation'
            })

          }

        }

    }

    if (!conversation) return null

    try {

      await conversation.add(id)

    } catch (e) {

      addBreadcrumb({
        message: 'User not added when creating conversation'
      })

    }

    return conversation.uniqueName

  }

  joinConversation = async (conversationName: string): Promise<Conversation | false | Error> => {

    if (this.remoteClient === null) return false

    let conversation: Conversation

    try {

      conversation = await this.remoteClient.getConversationByUniqueName(conversationName)

    }
    catch (e) {

      addBreadcrumb({
        message: 'Could not find the conversation'
      })
      captureException(e)

      return false

    }

    if (conversation.status !== 'joined') {

      try {

        await conversation.join()

      }
      catch (e) {

        addBreadcrumb({
          message: 'Could not join the conversation'
        })
        captureException(e)
        throw new Error(e)
      }

    }

    this.joinedConversations.push(conversation)

    conversation.on('messageAdded', (message: Message) =>
      this.emit('messageAdded', this.formatMessageResponse(message))
    )

    conversation.on('typingStarted', (participant: Participant) =>
      this.emit('typingStarted',{
        conversationName: participant.conversation.uniqueName,
        author: participant.identity
      })
    )

    conversation.on('typingEnded', (participant: Participant) =>
      this.emit('typingEnded', {
        conversationName: participant.conversation.uniqueName,
        author: participant.identity
      })
    )

    return conversation
  }
}

export default TwilioChatClient
